import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  fetchLegalConfig,
  lireLeConsentement,
  needsConsent,
  recordConsent,
  rejouerLeConsentement,
  type BesoinDeConsentement,
  type ConfigLegale,
} from '../../firebase/legal';
import { BrandScreen } from '../../components/layout/Screens';
import { Button } from '../../components/ui/Button';
import { SiroccoLogo } from '../../theme/illustrations';
import { useSession } from '../../store/SessionContext';
import { LienLegal } from './LienLegal';
import { t } from '../../i18n';

/**
 * La porte du reconsentement.
 *
 * Quand un document change sur le fond, on ne peut pas laisser
 * quelqu'un continuer sous l'ancien accord : l'ecran se dresse, plein
 * cadre, et ne se contourne pas. Il n'a que deux issues — accepter, ou
 * se deconnecter. Pas de « plus tard » : un « plus tard » qu'on repousse
 * indefiniment revient a n'avoir jamais demande.
 *
 * # Ce qui prime sur tout le reste
 *
 * Personne ne doit rester enferme dehors a cause du reseau. Si la
 * configuration est injoignable et qu'aucun cache n'existe, la porte
 * s'efface. Et si l'ecriture du consentement echoue, on laisse passer
 * quand meme : le consentement est garde localement et rejoue plus
 * tard. Un refus d'ecriture n'est pas un refus de consentir.
 *
 * # Quand la question se pose
 *
 * Au lancement, et a chaque retour au premier plan. Une application
 * mobile vit des jours sans etre relancee : sans le second controle, une
 * version publiee ce matin ne serait vue que la semaine prochaine.
 */
export function ConsentGate({ children }: { children: ReactNode }) {
  const { user, logout } = useSession();
  const uid = user?.id ?? null;

  const [config, setConfig] = useState<ConfigLegale | null>(null);
  const [besoin, setBesoin] = useState<BesoinDeConsentement | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const examiner = useCallback(async () => {
    if (!uid) {
      setBesoin({ type: 'aucun' });
      return;
    }
    const [configuration, consentement] = await Promise.all([
      fetchLegalConfig(),
      lireLeConsentement(uid),
    ]);
    setConfig(configuration);
    setBesoin(needsConsent(configuration, consentement));
    // Un consentement que le reseau avait emporte repart d'ici.
    void rejouerLeConsentement(uid);
  }, [uid]);

  useEffect(() => {
    void examiner();

    const auRetour = () => {
      if (!document.hidden) void examiner();
    };
    document.addEventListener('visibilitychange', auRetour);
    return () => document.removeEventListener('visibilitychange', auRetour);
  }, [examiner]);

  /*
    Tant qu'on ne sait pas, on laisse voir l'application. L'inverse —
    un ecran d'attente le temps de decider — ferait clignoter un voile
    gris a chaque retour au premier plan, pour une question dont la
    reponse est « non » dans l'immense majorite des cas.
  */
  if (!besoin || besoin.type !== 'reconsentement' || !config) return <>{children}</>;

  const change = besoin.documents;

  async function accepter() {
    if (!uid || !config) return;
    setEnvoi(true);
    // Le resultat ne conditionne pas le passage : un echec d'ecriture
    // est rejoue, il n'a pas a retenir quelqu'un devant un mur.
    await recordConsent(uid, config);
    setBesoin({ type: 'aucun' });
    setEnvoi(false);
  }

  return (
    <BrandScreen>
      <div className="auth auth--register consent">
        <div className="auth__logo">
          <SiroccoLogo markSize={50} />
        </div>

        <h1 className="auth__title">
          {change.length > 1 ? t.legal.consentement.titrePlusieurs : t.legal.consentement.titreUn}
        </h1>

        <p className="auth__switch auth__switch--top">
          {change.length > 1
            ? t.legal.consentement.textePlusieurs
            : t.legal.consentement.texteUn}
        </p>

        <ul className="consent__liste">
          {change.includes('cgu') && (
            <li>
              <LienLegal url={config.cgu.url} className="consent__doc">
                {t.legal.documents.cgu}
                <span>{t.legal.documents.version(config.cgu.version)}</span>
              </LienLegal>
            </li>
          )}
          {change.includes('privacy') && (
            <li>
              <LienLegal url={config.privacy.url} className="consent__doc">
                {t.legal.documents.confidentialite}
                <span>{t.legal.documents.version(config.privacy.version)}</span>
              </LienLegal>
            </li>
          )}
        </ul>

        <Button
          variant="brand"
          block
          className="auth__submit"
          loading={envoi}
          onClick={() => void accepter()}
        >
          {t.legal.consentement.accepter}
        </Button>

        <button type="button" className="auth__link" onClick={() => void logout()}>
          {t.legal.consentement.deconnecter}
        </button>
      </div>
    </BrandScreen>
  );
}
